import { readableColor, shade, tint } from "polished";
import type { FC } from "react";
import { Link, type To } from "react-router-dom";

import { Dialog, HStack, Icons, SymbolIcon, TriggerButton } from "widgets";

import style from "./styles/ContextWidget.module.scss";

import { ProjectColoredLink } from "@/modules/projects/components/ProjectColoredWidgets.js";
import { getCssColor } from "../../../app-utils/color.js";
import type { Group, Project, View } from "../../../graphql.js";
import { getGroupRoute, getViewRoute } from "../../../redux/modules/saved-view.js";
import { PopoverController } from "../overlays/components/OverlayControllers.js";

export enum ContextWidgetType {
  SAVED_VIEW = "SAVED_VIEW",
  SAVED_GROUP = "SAVED_GROUP",
}

export interface INavigationContextProps {
  type: ContextWidgetType;
  data?: Group | View;
}

interface ContextWidgetTooltipData {
  type: ContextWidgetType;
  typeLabel?: string;
  project: Project;
  title: string;
  description: string;
  route?: To;
  private: boolean;
}

export default function ContextWidget({ type, data }: INavigationContextProps) {
  if (!data.project) {
    return null;
  }

  const contextData = getContextData(type, data);
  const jsx = getTypeJsxElements(type);

  const bgColor = getCssColor(contextData.project.color);
  const txtColor = readableColor(bgColor, shade(0.6, bgColor), tint(0.6, bgColor), false);

  return (
    <PopoverController
      overlayOptions={{ placement: "right" }}
      actionWidget={(controllerProps) => (
        <TriggerButton {...controllerProps} showLabel>
          <div className={style.widget}>
            {contextData.private && (
              <SymbolIcon icon={"lock"} className={style.widget_lock_icon} aria-label={"private"} />
            )}
            <div className={style.widget_icon}>{jsx.icon}</div>
            <div className={style.project_tag} style={{ backgroundColor: bgColor, color: txtColor }}>
              {contextData.project.name.slice(0, 2).toUpperCase()}
            </div>
          </div>
        </TriggerButton>
      )}
    >
      {(controllerProps) => (
        <Dialog {...controllerProps}>
          <ContextWidgetDetails typeElements={jsx} contextData={contextData} />
        </Dialog>
      )}
    </PopoverController>
  );
}

interface ContextWidgetDetailsProps {
  typeElements: TypeJsxElements;
  contextData: ContextWidgetTooltipData;
}

const ContextWidgetDetails: FC<ContextWidgetDetailsProps> = (props) => {
  const { typeElements, contextData } = props;
  return (
    <div className={style.widget_details}>
      <HStack className={style.widget_details_type}>
        <span className={style.widget_details_icon_alignment} aria-hidden="true">
          {typeElements.icon}
        </span>
        <span className={style.widget_details_type_title}>{contextData.typeLabel || typeElements.text}</span>
      </HStack>
      <ProjectColoredLink project={contextData.project} textClassName={style.widget_details_project} />
      {contextData.title && (
        <>
          <HStack className={style.widget_details_title}>
            {contextData.private && (
              <SymbolIcon icon={"lock"} className={style.widget_details_icon_alignment} aria-label={"private"} />
            )}
            {contextData.route ? <Link to={contextData.route}>{contextData.title}</Link> : contextData.title}
          </HStack>
          {contextData.description && <div className={style.widget_details_description}>{contextData.description}</div>}
        </>
      )}
    </div>
  );
};

function getContextData(type: ContextWidgetType, data: INavigationContextProps["data"]): ContextWidgetTooltipData {
  switch (type) {
    case ContextWidgetType.SAVED_GROUP:
      return {
        type,
        project: data.project,
        title: data.name,
        description: data.description,
        route: getGroupRoute(data as Group),
        private: !data.shared,
      };
    case ContextWidgetType.SAVED_VIEW:
      return {
        type,
        typeLabel: (data as View).viewLabel,
        project: data.project,
        title: data.name,
        description: data.description,
        route: getViewRoute(data as View),
        private: data.shared === false,
        // tabTitle: `${view.name} [${viewLabel || view.viewLabel}] - Selector`,
      };
  }
}

interface TypeJsxElements {
  icon: JSX.Element;
  text: string;
}

function getTypeJsxElements(type: ContextWidgetType): TypeJsxElements {
  switch (type) {
    case ContextWidgetType.SAVED_GROUP:
      return {
        icon: <img src={Icons.Action.SavedItems} width={20} height={20} alt={"Saved Group"} />,
        text: "Group",
      };
    case ContextWidgetType.SAVED_VIEW:
      return {
        icon: <img src={Icons.Action.SavedItems} width={20} height={20} alt={"Saved View"} />,
        text: "Saved View",
      };
    default:
      return {
        icon: null,
        text: "",
      };
  }
}
